/**
 * Workspace Controls — platform settings page for the Vault Holder
 * workspace concept. Lets the user pick the active workspace, manage
 * members/roles/invitations, create new org workspaces, and (owner-only)
 * delete the current one.
 *
 * URL: /workspaces. Selection is the same platform-wide localStorage
 * key the global HolderSwitcher uses, so changing the workspace here
 * also changes it in the header chip, My Data, Fias 360, etc.
 */

import { useEffect, useId, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, Lock, User, Trash2, Mail, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';
import { useHolderSwitcher } from '@/components/HolderSwitcher';
import { ConnectedAccountsCard } from '../components/ConnectedAccountsCard';
import {
  useWorkspaceMembers,
  useWorkspaceInvitations,
  useInviteWorkspaceMember,
  useCancelWorkspaceInvitation,
  useUpdateWorkspaceMemberRole,
  useRevokeWorkspaceMember,
  useDeleteWorkspace,
  useRenameWorkspace,
  useCreateWorkspace,
  useMyVaultInvitations,
  useAcceptVaultInvitation,
  useDeclineMyVaultInvitation,
} from '../hooks/useWorkspaceTeam';
import { markInvitationsSeen } from '../hooks/useInvitationAlerts';
import { useCanCreateVaultNamedLabel, useVaultNamedLabels } from '../hooks/useVaultNamedLabels';
import {
  useCanCreateVaultWorkspaceGroup,
  useVaultWorkspaceGroups,
} from '../hooks/useVaultWorkspaceGroups';
import { namedLabelsPath, workspaceGroupsPath } from './named-labels/shared';
import { UserSearchCombobox } from '@/components/UserSearchCombobox';
import {
  ApprovalsCard,
  CompanyCard,
  WorkspaceCompanyProposal,
  WORKSPACE_COMPANY_CARD_ID,
} from '../components/WorkspaceCompanySections';
import { useMyCompanyLinkProposals } from '../hooks/useCompanies';
import {
  invitationRecipientLabel,
  isInvitationExpired,
  parseInviteRecipient,
  resendInvitePayload,
} from '@/lib/vault-invitations';
import type { VaultHolderMemberDto, VaultInvitationDto } from '@/lib/api';

type Role = 'owner' | 'admin' | 'member' | 'viewer';

const ROLE_OPTIONS: ReadonlyArray<{ value: Role; label: string; description: string }> = [
  { value: 'owner', label: 'Owner', description: 'Full control; can manage team and data' },
  { value: 'admin', label: 'Admin', description: 'Manage team and data; cannot delete workspace' },
  { value: 'member', label: 'Member', description: 'Read + write data; cannot manage team' },
  { value: 'viewer', label: 'Viewer', description: 'Read-only — perfect for support reps' },
];

/**
 * Pending invitations the caller has received, shown at the very top of the
 * page. Landing here marks them seen, which clears the alert dots on the user's
 * name and the Workspace Controls menu entry.
 */
/**
 * Company link proposals on workspaces the user OWNS, each with the same
 * disclosure and owners checkbox the workspace's Company card shows (read
 * from that workspace, since the feed row does not carry it), so the owner
 * can act here; "Open <workspace>" selects it and scrolls to that card.
 * Landing here marks them seen.
 */
function PendingCompanyProposalsBanner({ onReview }: { onReview: (holderId: string) => void }) {
  const proposalsQuery = useMyCompanyLinkProposals();
  const list = proposalsQuery.data ?? [];

  useEffect(() => {
    const ids = (proposalsQuery.data ?? []).map((p) => p.invitationId);
    if (ids.length > 0) markInvitationsSeen(ids);
  }, [proposalsQuery.data]);

  if (proposalsQuery.isLoading || list.length === 0) return null;

  return (
    <Card className="border-primary/40 bg-primary/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Building2 className="h-5 w-5 text-primary" aria-hidden="true" />
          Company invitations
        </CardTitle>
        <CardDescription>
          A Company has invited a workspace you own to join it. Linking moves its billing to the
          Company and lets Company-approved arches work with its data; only the Company can unlink
          it. Review before accepting.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {list.map((p) => (
          <div key={p.invitationId} className="space-y-2">
            <WorkspaceCompanyProposal
              holderId={p.holderId}
              invitationId={p.invitationId}
              workspaceName={p.holderDisplayName}
            />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                onReview(p.holderId);
                // The Company card sits below the team cards; bring it into view.
                window.setTimeout(
                  () =>
                    document
                      .getElementById(WORKSPACE_COMPANY_CARD_ID)
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
                  50,
                );
              }}
            >
              Open {p.holderDisplayName}
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function PendingInvitationsBanner() {
  const invitationsQuery = useMyVaultInvitations();
  const accept = useAcceptVaultInvitation();
  const decline = useDeclineMyVaultInvitation();
  const list = invitationsQuery.data ?? [];

  useEffect(() => {
    const ids = (invitationsQuery.data ?? []).map((i) => i.invitationId);
    if (ids.length > 0) markInvitationsSeen(ids);
  }, [invitationsQuery.data]);

  if (invitationsQuery.isLoading || list.length === 0) return null;

  async function handleAccept(id: string, name: string): Promise<void> {
    try {
      await accept.mutateAsync(id);
      toast({ title: `Joined ${name}` });
    } catch (err) {
      toast({
        title: 'Failed to accept invitation',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  }

  async function handleDecline(id: string): Promise<void> {
    try {
      await decline.mutateAsync(id);
      toast({ title: 'Invitation declined' });
    } catch (err) {
      toast({
        title: 'Failed to decline invitation',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  }

  return (
    <Card className="border-primary/40 bg-primary/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Mail className="h-5 w-5 text-primary" />
          Pending invitations
        </CardTitle>
        <CardDescription>You&apos;ve been invited to these workspaces.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {list.map((inv) => (
          <div
            key={inv.invitationId}
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2"
          >
            <p className="text-sm text-foreground">
              <span className="font-medium">{inv.holderDisplayName}</span> — invited as{' '}
              <span className="capitalize">{inv.role}</span>
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => handleAccept(inv.invitationId, inv.holderDisplayName)}
                disabled={accept.isPending}
              >
                Accept
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleDecline(inv.invitationId)}
                disabled={decline.isPending}
              >
                Decline
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export function WorkspaceControlsPage() {
  const switcher = useHolderSwitcher({ defaultToPersonal: true });

  return (
    <div>
      <div className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl font-bold text-foreground">Workspace Controls</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Switch workspaces, invite teammates, and manage roles.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8 space-y-6">
        <PendingInvitationsBanner />
        <PendingCompanyProposalsBanner onReview={switcher.setSelectedHolderId} />

        <ActiveWorkspaceCard
          options={switcher.options}
          selectedHolderId={switcher.selectedHolderId}
          onSelect={switcher.setSelectedHolderId}
          isLoading={switcher.isLoading}
        />

        <CreateWorkspaceCard onCreated={switcher.setSelectedHolderId} />

        {/* This page manages personal/org workspaces; it does not opt into
            guild spaces, so the guild exclusion is type narrowing only. */}
        {switcher.selectedOption && switcher.selectedOption.holderType !== 'guild' && (
          <WorkspaceDetailSections
            holderId={switcher.selectedOption.holderId}
            holderType={switcher.selectedOption.holderType}
            displayName={switcher.selectedOption.displayName}
            myRole={switcher.selectedOption.role}
            myPrincipalId={switcher.principalId}
            onWorkspaceDeleted={() => switcher.setSelectedHolderId(null)}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Active workspace picker
// ---------------------------------------------------------------------------

function ActiveWorkspaceCard({
  options,
  selectedHolderId,
  onSelect,
  isLoading,
}: {
  options: ReturnType<typeof useHolderSwitcher>['options'];
  selectedHolderId: string | null;
  onSelect: (holderId: string | null) => void;
  isLoading: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Active Workspace</CardTitle>
        <CardDescription>
          The active workspace is shared across My Data, the global header, and every other
          workspace-aware surface.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading workspaces…</p>
        ) : options.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No workspaces available. Create one below to get started.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-border border border-border rounded-md">
            {options.map((o) => {
              const isActive = o.holderId === selectedHolderId;
              const Icon = o.holderType === 'org' ? Building2 : User;
              return (
                <li key={o.holderId}>
                  <button
                    type="button"
                    onClick={() => onSelect(o.holderId)}
                    className={
                      'w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-accent transition-colors ' +
                      (isActive ? 'bg-accent/50' : '')
                    }
                    aria-pressed={isActive}
                  >
                    <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="flex-1 truncate text-sm text-foreground">{o.displayName}</span>
                    {o.role !== 'self' && (
                      <span className="text-xs text-muted-foreground capitalize">{o.role}</span>
                    )}
                    {isActive && (
                      <span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary">
                        Active
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Create workspace
// ---------------------------------------------------------------------------

function CreateWorkspaceCard({ onCreated }: { onCreated: (holderId: string) => void }) {
  const [name, setName] = useState('');
  const create = useCreateWorkspace();

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const result = await create.mutateAsync(trimmed);
      onCreated(result.holder.holderId);
      setName('');
      toast({ title: 'Workspace created', description: result.holder.displayName });
    } catch (err) {
      toast({
        title: 'Failed to create workspace',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create a new workspace</CardTitle>
        <CardDescription>
          Spin up a shared workspace for an organization, team, or project.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[14rem] space-y-1">
            <Label htmlFor="new-workspace-name">Workspace name</Label>
            <input
              id="new-workspace-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Inc."
              maxLength={200}
              className="w-full rounded border border-border bg-card px-2 py-1.5 text-sm"
            />
          </div>
          <Button type="submit" disabled={!name.trim() || create.isPending}>
            {create.isPending ? 'Creating…' : 'Create workspace'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Workspace detail (info + team + danger zone)
// ---------------------------------------------------------------------------

function WorkspaceDetailSections({
  holderId,
  holderType,
  displayName,
  myRole,
  myPrincipalId,
  onWorkspaceDeleted,
}: {
  holderId: string;
  holderType: 'personal' | 'org';
  displayName: string;
  myRole: 'self' | 'owner' | 'admin' | 'member' | 'viewer';
  myPrincipalId: string | null;
  onWorkspaceDeleted: () => void;
}) {
  const isOrg = holderType === 'org';
  const canManage = isOrg && (myRole === 'owner' || myRole === 'admin');
  const canDelete = isOrg && myRole === 'owner';
  // Renaming is an owner-level identity change, and only shared (org)
  // workspaces have a user-editable name — a personal workspace's name is
  // derived from the owning user.
  const canRename = isOrg && myRole === 'owner';

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Workspace details</CardTitle>
          <CardDescription>
            {isOrg
              ? 'Shared workspace — invite teammates and manage their access below.'
              : 'Your personal workspace. Single-owner by design; nothing here to share.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {canRename ? (
            <WorkspaceNameField holderId={holderId} displayName={displayName} />
          ) : (
            <div className="grid gap-1">
              <Label className="text-muted-foreground">Name</Label>
              <p className="text-foreground">{displayName}</p>
            </div>
          )}
          <div className="grid gap-1">
            <Label className="text-muted-foreground">Type</Label>
            <p className="text-foreground capitalize">{holderType}</p>
          </div>
          <div className="grid gap-1">
            <Label className="text-muted-foreground">Your role</Label>
            <p className="text-foreground capitalize">
              {myRole === 'self' ? 'Owner (you)' : myRole}
            </p>
          </div>
          <div className="grid gap-1">
            <Label className="text-muted-foreground">Workspace ID</Label>
            <p className="text-foreground font-mono text-sm">{holderId}</p>
          </div>
        </CardContent>
      </Card>

      {isOrg && (
        <TeamCard holderId={holderId} canManage={canManage} myPrincipalId={myPrincipalId} />
      )}

      {isOrg && canManage && <AgentAccessCard holderId={holderId} />}

      {/* ADR-029 §2: outside accounts this workspace connected, personal or org. */}
      <ConnectedAccountsCard holderId={holderId} />

      {/* ADR-022: the workspace side of Companies + arche approvals. Every member
          may see them; owners accept links, owners/admins decide approvals. */}
      {isOrg && <CompanyCard holderId={holderId} isOwner={myRole === 'owner'} />}
      {isOrg && <ApprovalsCard holderId={holderId} canManage={canManage} />}

      {/* ADR-039: every seat may open it; members see only labels they're on. */}
      {isOrg && <NamedLabelsLinkCard holderId={holderId} canManage={canManage} />}
      {/* ADR-041: every seat sees every group's name; members see who's in their own.
          Shown once groups exist here or may be made (named labels in use or allowed). */}
      {isOrg && <WorkspaceGroupsLinkCard holderId={holderId} canManage={canManage} />}

      {canDelete && (
        <DangerZoneCard
          holderId={holderId}
          displayName={displayName}
          onDeleted={onWorkspaceDeleted}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Named labels (ADR-039) — a link to the workspace's Labels page
// ---------------------------------------------------------------------------

export function NamedLabelsLinkCard({
  holderId,
  canManage,
}: {
  holderId: string;
  canManage: boolean;
}) {
  const labels = useVaultNamedLabels(holderId);
  const canCreate = useCanCreateVaultNamedLabel(holderId);
  // Nothing to open: no label is visible here and none may be made yet (the
  // rollout switch admits a workspace's first label) — so no card at all.
  if ((labels.data ?? []).length === 0 && canCreate.data !== true) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock className="h-4 w-4" aria-hidden="true" />
          Named labels
        </CardTitle>
        <CardDescription>
          {canManage
            ? 'Keep documents to a named group of people, like “Board” or “HR”, on top of their sensitivity.'
            : 'The labels you’re on, requests waiting for you, and documents you filed into a drop box.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button size="sm" variant="outline" asChild>
          <Link to={namedLabelsPath(holderId)}>Open named labels</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Workspace groups (ADR-041) — a link to the workspace's Groups page
// ---------------------------------------------------------------------------

export function WorkspaceGroupsLinkCard({
  holderId,
  canManage,
}: {
  holderId: string;
  canManage: boolean;
}) {
  const groups = useVaultWorkspaceGroups(holderId);
  const canCreate = useCanCreateVaultWorkspaceGroup(holderId);
  // Groups exist to read named labels: no card while there are none here and
  // none may be made (labels not in use, and the rollout not admitting them).
  if ((groups.data ?? []).length === 0 && canCreate.data !== true) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-4 w-4" aria-hidden="true" />
          Groups
        </CardTitle>
        <CardDescription>
          {canManage
            ? 'Name a team once, like “Board” or “HR”, and put it on a named label instead of adding people one by one.'
            : 'The groups in this workspace, and the ones you’re in.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button size="sm" variant="outline" asChild>
          <Link to={workspaceGroupsPath(holderId)}>Open groups</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Editable workspace name (owner-only, org-only)
// ---------------------------------------------------------------------------

export function WorkspaceNameField({
  holderId,
  displayName,
}: {
  holderId: string;
  displayName: string;
}) {
  const rename = useRenameWorkspace();
  const inputId = useId();
  const [value, setValue] = useState(displayName);

  // Re-sync when switching workspaces or after an external update.
  useEffect(() => {
    setValue(displayName);
  }, [displayName]);

  const trimmed = value.trim();
  const dirty = trimmed !== displayName && trimmed.length > 0;

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!dirty) return;
    try {
      await rename.mutateAsync({ holderId, displayName: trimmed });
      toast({ title: 'Workspace renamed', description: trimmed });
    } catch (err) {
      toast({
        title: 'Failed to rename workspace',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  }

  return (
    <form onSubmit={handleSubmit} className="grid gap-1">
      <Label htmlFor={inputId} className="text-muted-foreground">
        Name
      </Label>
      <div className="flex flex-wrap items-center gap-2">
        <input
          id={inputId}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          maxLength={200}
          className="flex-1 min-w-[14rem] rounded border border-border bg-card px-2 py-1.5 text-sm"
        />
        <Button type="submit" size="sm" disabled={!dirty || rename.isPending}>
          {rename.isPending ? 'Saving…' : 'Save'}
        </Button>
        {dirty && !rename.isPending && (
          <Button type="button" size="sm" variant="ghost" onClick={() => setValue(displayName)}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Team card (members + pending invitations + invite form)
// ---------------------------------------------------------------------------

/**
 * AgentAccessCard — which external agents (Claude Code and other MCP clients)
 * reach THIS workspace's shared data.
 *
 * A member can bind an API key to a workspace (Account Settings -> API Keys),
 * pointing an agent at data the whole team shares. Without this panel that is
 * invisible to everyone but the member who did it, and "what non-human things
 * read our data?" is a question the people accountable for that data should be
 * able to answer.
 *
 * Owner/admin only, and rendered by the caller's `canManage` — one member's
 * key metadata becoming visible to a colleague is a deliberate disclosure,
 * scoped to the people accountable rather than the whole team.
 *
 * Read-only on purpose. Access already follows membership: the agent-tools
 * plane re-authorizes on EVERY call, so removing someone from the workspace
 * stops their agent on its next call, with no key revocation and nothing to
 * wait out. Revoking another user's credential from here would be a different
 * authority model.
 */
function AgentAccessCard({ holderId }: { holderId: string }) {
  const query = useQuery({
    queryKey: queryKeys.apiKeys.forWorkspace(holderId),
    queryFn: () => api.listWorkspaceAgentKeys(holderId),
  });
  const agentKeys = query.data?.agentKeys ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Agent access</CardTitle>
        <CardDescription>
          External AI agents that members have connected to this workspace. Each one acts as the
          member who created it and can never do more than that member&apos;s role allows. Removing
          someone from the team stops their agents immediately.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {query.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

        {!query.isLoading && agentKeys.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No agents are connected to this workspace.
          </p>
        )}

        {agentKeys.length > 0 && (
          <ul className="flex flex-col divide-y divide-border border-y border-border">
            {agentKeys.map((k) => (
              <li key={k.keyId} className="flex items-center justify-between gap-4 py-3">
                <div className="min-w-0">
                  <p className="font-medium truncate">{k.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {k.ownerDisplayName ?? k.ownerUsername ?? 'Unknown member'} ·{' '}
                    {k.lastUsedAt
                      ? `last used ${new Date(k.lastUsedAt).toLocaleDateString()}`
                      : 'never used'}
                  </p>
                </div>
                <Badge variant={k.canWrite ? 'default' : 'secondary'}>
                  {k.canWrite ? 'Can make changes' : 'Read only'}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function TeamCard({
  holderId,
  canManage,
  myPrincipalId,
}: {
  holderId: string;
  canManage: boolean;
  myPrincipalId: string | null;
}) {
  const membersQuery = useWorkspaceMembers(holderId);
  const invitationsQuery = useWorkspaceInvitations(canManage ? holderId : null);
  const activeMembers = membersQuery.data?.filter((m) => m.status === 'active') ?? [];
  const revokedMembers = membersQuery.data?.filter((m) => m.status !== 'active') ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Team</CardTitle>
        <CardDescription>
          Invite teammates and control what each person can do. Viewer is read-only and ideal for
          support reps; Member is read + write; Admin can manage the team; Owner can also delete the
          workspace.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {canManage && <InviteForm holderId={holderId} />}

        <section>
          <h3 className="text-sm font-medium mb-2">Active members ({activeMembers.length})</h3>
          {membersQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading members…</p>
          ) : activeMembers.length === 0 ? (
            <p className="text-sm text-muted-foreground">No active members yet.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-border border-y border-border">
              {activeMembers.map((m) => (
                <MemberRow
                  key={m.membershipId}
                  member={m}
                  isSelf={m.principalId === myPrincipalId}
                  canManage={canManage}
                  holderId={holderId}
                />
              ))}
            </ul>
          )}
        </section>

        {canManage && (
          <PendingInvitations holderId={holderId} invitationsQuery={invitationsQuery} />
        )}

        {revokedMembers.length > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer text-muted-foreground">
              Past members ({revokedMembers.length})
            </summary>
            <ul className="mt-2 flex flex-col divide-y divide-border">
              {revokedMembers.map((m) => {
                const label =
                  m.displayName ??
                  (m.username ? `@${m.username}` : null) ??
                  `Principal …${m.principalId.slice(-6)}`;
                return (
                  <li
                    key={m.membershipId}
                    className="flex justify-between py-2 text-xs text-muted-foreground"
                  >
                    <span className="truncate">{label}</span>
                    <span>
                      {m.status} · was {m.role}
                    </span>
                  </li>
                );
              })}
            </ul>
          </details>
        )}
      </CardContent>
    </Card>
  );
}

function InviteForm({ holderId }: { holderId: string }) {
  const invite = useInviteWorkspaceMember(holderId);
  const [recipient, setRecipient] = useState('');
  const [role, setRole] = useState<Role>('viewer');
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    // Auto-detect the invite kind (email vs username — typed or picked
    // from the autocomplete; the server resolves either).
    const parsed = parseInviteRecipient(recipient);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    try {
      await invite.mutateAsync({ ...parsed.payload, role });
      setRecipient('');
      toast({ title: 'Invitation sent', description: `${parsed.label} (${role})` });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invite failed';
      setError(message);
      toast({ title: 'Failed to send invitation', description: message, variant: 'destructive' });
    }
  }

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-2 rounded border border-border bg-muted/30 p-4"
    >
      <h3 className="text-sm font-medium">Invite a teammate</h3>
      <div className="flex flex-wrap gap-2 items-end">
        <label className="flex flex-col gap-1 flex-1 min-w-[14rem]">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            Email or username
          </span>
          <UserSearchCombobox
            value={recipient}
            onValueChange={setRecipient}
            required
            className="rounded border border-border bg-card px-2 py-1.5 text-sm w-full"
          />
        </label>
        <label className="flex flex-col gap-1 w-44">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">Role</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            className="rounded border border-border bg-card px-2 py-1.5 text-sm"
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
        <Button type="submit" disabled={invite.isPending || !recipient.trim()}>
          {invite.isPending ? 'Sending…' : 'Send invitation'}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        {ROLE_OPTIONS.find((r) => r.value === role)?.description}
      </p>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </form>
  );
}

function MemberRow({
  member,
  isSelf,
  canManage,
  holderId,
}: {
  member: VaultHolderMemberDto;
  isSelf: boolean;
  canManage: boolean;
  holderId: string;
}) {
  const updateRole = useUpdateWorkspaceMemberRole(holderId);
  const revoke = useRevokeWorkspaceMember(holderId);
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);

  async function changeRole(next: Role): Promise<void> {
    if (next === member.role) return;
    try {
      await updateRole.mutateAsync({ membershipId: member.membershipId, role: next });
      toast({ title: 'Role updated' });
    } catch (err) {
      toast({
        title: 'Failed to update role',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  }

  async function handleRevoke(): Promise<void> {
    try {
      await revoke.mutateAsync(member.membershipId);
      toast({ title: 'Member removed' });
    } catch (err) {
      toast({
        title: 'Failed to remove member',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setConfirmingRevoke(false);
    }
  }

  const primaryLabel =
    member.displayName ?? (member.username ? `@${member.username}` : null) ?? null;
  const fallbackLabel = `Principal …${member.principalId.slice(-8)}`;

  return (
    <li className="flex items-center justify-between py-3 gap-3">
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground truncate">
          {primaryLabel ?? fallbackLabel}
          {primaryLabel && member.username && member.displayName && (
            <span className="ml-2 text-xs text-muted-foreground">@{member.username}</span>
          )}
          {isSelf && <span className="ml-2 text-xs text-muted-foreground">(you)</span>}
        </p>
        <p className="text-xs text-muted-foreground">
          Joined {new Date(member.grantedAt).toLocaleDateString()}
          {member.expiresAt && ` · expires ${new Date(member.expiresAt).toLocaleDateString()}`}
        </p>
      </div>
      {canManage && !isSelf ? (
        <>
          <select
            value={member.role}
            onChange={(e) => changeRole(e.target.value as Role)}
            disabled={updateRole.isPending}
            className="rounded border border-border bg-card px-2 py-1 text-xs"
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConfirmingRevoke(true)}
            disabled={revoke.isPending}
            className="text-destructive hover:text-destructive"
          >
            Remove
          </Button>
          <AlertDialog
            open={confirmingRevoke}
            onOpenChange={(open) => !open && setConfirmingRevoke(false)}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove this member?</AlertDialogTitle>
                <AlertDialogDescription>
                  {primaryLabel ?? fallbackLabel} will lose access to this workspace. You can
                  re-invite them later.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={revoke.isPending}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  disabled={revoke.isPending}
                  onClick={(e) => {
                    e.preventDefault();
                    handleRevoke();
                  }}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {revoke.isPending ? 'Removing…' : 'Remove'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      ) : (
        <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground capitalize">
          {member.role}
        </span>
      )}
    </li>
  );
}

/** Confirm-dialog copy: a live invitation is cancelled, an expired one removed. */
const INVITATION_DIALOG_COPY = {
  cancel: {
    title: 'Cancel invitation?',
    body: (recipient: string) =>
      `${recipient} will no longer be able to accept this invitation. You can send a new one anytime.`,
    action: 'Cancel invitation',
    pending: 'Cancelling…',
  },
  remove: {
    title: 'Remove expired invitation?',
    body: (recipient: string) =>
      `The expired invitation to ${recipient} will be removed from this list. You can send a new one anytime.`,
    action: 'Remove invitation',
    pending: 'Removing…',
  },
} as const;

export function PendingInvitations({
  holderId,
  invitationsQuery,
}: {
  holderId: string;
  invitationsQuery: ReturnType<typeof useWorkspaceInvitations>;
}) {
  const cancel = useCancelWorkspaceInvitation(holderId);
  const invite = useInviteWorkspaceMember(holderId);
  // The dialog's target outlives `dialogOpen` so its copy doesn't change
  // mid close-animation.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [target, setTarget] = useState<VaultInvitationDto | null>(null);
  const busy = cancel.isPending || invite.isPending;
  const copy = INVITATION_DIALOG_COPY[target && isInvitationExpired(target) ? 'remove' : 'cancel'];

  function confirmCancel(invitation: VaultInvitationDto): void {
    setTarget(invitation);
    setDialogOpen(true);
  }

  async function handleCancel(invitation: VaultInvitationDto): Promise<void> {
    const expired = isInvitationExpired(invitation);
    try {
      await cancel.mutateAsync(invitation.invitationId);
      toast({ title: expired ? 'Invitation removed' : 'Invitation cancelled' });
    } catch (err) {
      toast({
        title: expired ? 'Failed to remove invitation' : 'Failed to cancel invitation',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setDialogOpen(false);
    }
  }

  // Re-inviting replaces the expired row server-side, so no separate
  // cancel is needed first.
  async function handleResend(invitation: VaultInvitationDto): Promise<void> {
    const payload = resendInvitePayload(invitation);
    if (!payload) return;
    try {
      await invite.mutateAsync(payload);
      toast({ title: 'Invitation resent', description: invitationRecipientLabel(invitation) });
    } catch (err) {
      toast({
        title: 'Failed to resend invitation',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  }

  const invitations = invitationsQuery.data ?? [];
  if (invitationsQuery.isLoading) return null;
  if (invitations.length === 0) return null;

  return (
    <section>
      <h3 className="text-sm font-medium mb-2">Invitations ({invitations.length})</h3>
      <ul className="flex flex-col divide-y divide-border border-y border-border">
        {invitations.map((i) => {
          const expired = isInvitationExpired(i);
          const canResend = expired && resendInvitePayload(i) !== null;
          return (
            <li key={i.invitationId} className="flex items-center justify-between py-3 gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground truncate">{invitationRecipientLabel(i)}</p>
                <p className="text-xs text-muted-foreground">
                  Sent {new Date(i.createdAt).toLocaleDateString()} ·{' '}
                  {expired ? (
                    <span className="text-destructive">
                      expired {new Date(i.expiresAt).toLocaleDateString()}
                    </span>
                  ) : (
                    <>expires {new Date(i.expiresAt).toLocaleDateString()}</>
                  )}
                </p>
              </div>
              {expired && (
                <span className="text-xs px-2 py-0.5 rounded bg-destructive/10 text-destructive">
                  Expired
                </span>
              )}
              <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground capitalize">
                {i.role}
              </span>
              {canResend && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleResend(i)}
                  disabled={busy}
                  aria-label={`Resend invitation to ${invitationRecipientLabel(i)}`}
                >
                  Resend
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => confirmCancel(i)}
                disabled={busy}
                aria-label={`${expired ? 'Remove' : 'Cancel'} invitation to ${invitationRecipientLabel(i)}`}
                className="text-destructive hover:text-destructive"
              >
                {expired ? 'Remove' : 'Cancel'}
              </Button>
            </li>
          );
        })}
      </ul>
      <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{copy.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {target ? copy.body(invitationRecipientLabel(target)) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancel.isPending}>Keep it</AlertDialogCancel>
            <AlertDialogAction
              disabled={cancel.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (target) handleCancel(target);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {cancel.isPending ? copy.pending : copy.action}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Danger zone — delete workspace (owner-only, org-only)
// ---------------------------------------------------------------------------

function DangerZoneCard({
  holderId,
  displayName,
  onDeleted,
}: {
  holderId: string;
  displayName: string;
  onDeleted: () => void;
}) {
  const deleteWorkspace = useDeleteWorkspace();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);

  async function handleDelete(): Promise<void> {
    try {
      const result = await deleteWorkspace.mutateAsync(holderId);
      onDeleted();
      toast({
        title: 'Workspace deleted',
        description: `Permanent deletion in ${result.graceDays} days.`,
      });
      setConfirming(false);
      navigate('/workspaces');
    } catch (err) {
      toast({
        title: 'Delete failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  }

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="text-destructive flex items-center gap-2">
          <Trash2 className="h-4 w-4" />
          Danger zone
        </CardTitle>
        <CardDescription>
          Deleting a workspace hides it immediately and permanently removes everything after a
          30-day grace window. Everyone with access loses it.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button variant="destructive" onClick={() => setConfirming(true)}>
          Delete workspace
        </Button>
      </CardContent>
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{displayName}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              This workspace and all of its data will be hidden immediately and permanently deleted
              after 30 days. Everyone with access will lose it. You can undo from the workspace
              switcher in the global header during the grace window.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteWorkspace.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteWorkspace.isPending}
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteWorkspace.isPending ? 'Deleting…' : 'Delete workspace'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export default WorkspaceControlsPage;
